/* src/contexts/profile/profileActionsFriends.js */
import { useCallback } from 'react';

// Helper to wrap cloud function calls (could be in a utils file too)
const callFriendFunctionInternal = async (funcRef, payload, actionName, currentUserId, setError) => {
    if (!currentUserId) {
        setError("Authentication required.");
        return null;
    }
    if (!funcRef) {
        setError(`${actionName} service unavailable.`);
        return null;
    }
    setError(null);

    try {
        const result = await funcRef(payload);
        if (result?.data?.success) {
            return result.data;
        } else {
            throw new Error(result?.data?.message || `Failed to ${actionName}.`);
        }
    } catch (err) {
        console.error(`Error calling ${actionName} function:`, err);
        const message = err.message || `An unexpected error occurred during ${actionName}.`;
        setError(message);
        return null;
    }
};

/**
 * Hook creator for Send Friend Request action.
 */
export const useCreateSendFriendRequestAction = (
    authUserRef,
    activeProfileIdState, // Read-only state needed
    setError,
    sendFriendRequestFn
) => {
    return useCallback(async (targetUsername) => {
        const currentUserId = authUserRef.current?.uid;
        if (!activeProfileIdState) { setError("No active profile selected."); return null; }
        const payload = { targetUsername, requestingProfileId: activeProfileIdState };
        return callFriendFunctionInternal(sendFriendRequestFn, payload, 'sendFriendRequest', currentUserId, setError);
    }, [authUserRef, activeProfileIdState, setError, sendFriendRequestFn]);
};

/**
 * Hook creator for Accept Friend Request action.
 */
export const useCreateAcceptFriendRequestAction = (
    authUserRef,
    activeProfileIdState, // Read-only state needed
    setError,
    acceptFriendRequestFn
) => {
    return useCallback(async (requestingProfileId) => {
        const currentUserId = authUserRef.current?.uid;
        if (!activeProfileIdState) { setError("No active profile selected."); return null; }
        const payload = { requestingProfileId: requestingProfileId, recipientProfileId: activeProfileIdState };
        return callFriendFunctionInternal(acceptFriendRequestFn, payload, 'acceptFriendRequest', currentUserId, setError);
    }, [authUserRef, activeProfileIdState, setError, acceptFriendRequestFn]);
};

/**
 * Hook creator for Reject Friend Request action.
 */
export const useCreateRejectFriendRequestAction = (
    authUserRef,
    activeProfileIdState, // Read-only state needed
    setError,
    handleFriendRequestFn
) => {
    return useCallback(async (profileIdToReject) => {
        const currentUserId = authUserRef.current?.uid;
        if (!activeProfileIdState) { setError("No active profile selected."); return null; }
        const payload = { profileIdToHandle: profileIdToReject, isReceivedRequest: true, userProfileId: activeProfileIdState };
        return callFriendFunctionInternal(handleFriendRequestFn, payload, 'rejectFriendRequest', currentUserId, setError);
    }, [authUserRef, activeProfileIdState, setError, handleFriendRequestFn]);
};

/**
 * Hook creator for Cancel Friend Request action.
 */
export const useCreateCancelFriendRequestAction = (
    authUserRef,
    activeProfileIdState, // Read-only state needed
    setError,
    handleFriendRequestFn
) => {
    return useCallback(async (profileIdToCancel) => {
        const currentUserId = authUserRef.current?.uid;
        if (!activeProfileIdState) { setError("No active profile selected."); return null; }
        const payload = { profileIdToHandle: profileIdToCancel, isReceivedRequest: false, userProfileId: activeProfileIdState };
        return callFriendFunctionInternal(handleFriendRequestFn, payload, 'cancelFriendRequest', currentUserId, setError);
    }, [authUserRef, activeProfileIdState, setError, handleFriendRequestFn]);
};

/**
 * Hook creator for Remove Friend action.
 */
export const useCreateRemoveFriendAction = (
    authUserRef,
    activeProfileIdState, // Read-only state needed
    setError,
    removeFriendFn
) => {
    return useCallback(async (friendProfileIdToRemove) => {
        const currentUserId = authUserRef.current?.uid;
        if (!activeProfileIdState) { setError("No active profile selected."); return null; }
        const payload = { friendProfileId: friendProfileIdToRemove, userProfileId: activeProfileIdState };
        return callFriendFunctionInternal(removeFriendFn, payload, 'removeFriend', currentUserId, setError);
    }, [authUserRef, activeProfileIdState, setError, removeFriendFn]);
};

/**
 * Hook creator for Search Users action.
 */
export const useCreateSearchUsersAction = (
    authUserRef,
    activeProfileIdState, // Read-only state needed
    setError,
    searchUsersFn
) => {
    return useCallback(async (searchQuery) => {
        const currentUserId = authUserRef.current?.uid;
        if (!activeProfileIdState) {
             setError("No active profile selected for search.");
             return [];
        }
        const payload = { searchTerm: searchQuery, requestingProfileId: activeProfileIdState };
        const resultData = await callFriendFunctionInternal(searchUsersFn, payload, 'searchUsers', currentUserId, setError);

        if (resultData && resultData.users && Array.isArray(resultData.users)) {
             return resultData.users;
        } else {
             console.warn("searchUsers context action did not receive a valid user list.", resultData);
             return [];
        }
    }, [authUserRef, activeProfileIdState, setError, searchUsersFn]);
};